import * as Sentry from '@sentry/browser';

/**
 * Sentry Logs for the web client.
 *
 * The app has no logger wrapper that reaches production — `devLog*` in ./logger only prints in
 * development — so the logging we actually have in the field is raw `console.*`. The SDK's
 * `consoleLoggingIntegration` captures exactly that, so no call sites change.
 *
 * Verified against the installed @sentry/core 10.71.0 (`build/esm/logs/`):
 *   - `consoleLoggingIntegration` does nothing at all unless `enableLogs` is truthy. It skips
 *     setup and only warns in debug builds, so logs are dropped SILENTLY without it.
 *   - Its `levels` option filters on CONSOLE METHOD names, not Sentry severities. `log` is sent
 *     as severity `info`, `assert` as `error`.
 *   - `sentry.environment` and `sentry.release` are stamped on every log from `Sentry.init`.
 */

type SentryLog = Parameters<NonNullable<Sentry.BrowserOptions['beforeSendLog']>>[0];

const SEVERITIES = ['trace', 'debug', 'info', 'warn', 'error'] as const;
type Severity = (typeof SEVERITIES)[number];

// Every console method the integration can hook, and the Sentry severity it is sent as.
const CONSOLE_METHOD_SEVERITY = {
  trace: 'trace',
  debug: 'debug',
  log: 'info',
  info: 'info',
  warn: 'warn',
  error: 'error',
  assert: 'error',
} as const satisfies Record<string, Severity>;
type ConsoleMethod = keyof typeof CONSOLE_METHOD_SEVERITY;

// Matches flowhawk-server's `SENTRY_LOG_LEVEL` default so both sources report the same floor.
const DEFAULT_LEVEL: Severity = 'info';

const MAX_DEPTH = 4;
const MAX_STRING_LENGTH = 1024;
// Browser stacks are typically 2-6 KB; the general limit would cut off the frames that matter.
const MAX_STACK_LENGTH = 8192;
// Upper bound on attributes created by splitting objects into fields, per log. An object that
// would push a log past it is kept as one JSON string instead, e.g. `console.error('x', state.cases)`.
const MAX_FLATTENED_ATTRIBUTES = 100;
const PARAMETER_PREFIX = 'sentry.message.parameter.';
// Added by the SDK in `_INTERNAL_captureLog` (`@sentry/core/build/esm/logs/internal.js`).
const SDK_USER_KEYS = new Set(['user.id', 'user.email', 'user.name']);

/**
 * True for values that came from the console call's arguments: parameters, and the keys the SDK
 * spreads in from a plain-object first argument. False for attributes the SDK adds itself
 * (`sentry.environment`, `sentry.release`, `user.*`, ...), which must pass through untouched.
 */
function isCallerKey(key: string): boolean {
  return key.startsWith(PARAMETER_PREFIX) || !(key.startsWith('sentry.') || SDK_USER_KEYS.has(key));
}

function isSeverity(value: string): value is Severity {
  return (SEVERITIES as readonly string[]).includes(value);
}

/**
 * An unrecognised level would otherwise select no console methods and forward nothing, while
 * looking correctly configured. Fall back to a working level and say so instead.
 */
export function resolveSentryLogLevel(configured: string | undefined): Severity {
  const level = (configured ?? '').trim().toLowerCase();
  if (!level) return DEFAULT_LEVEL;
  if (isSeverity(level)) return level;

  console.warn(
    `[sentry-logs] Ignoring unknown VITE_SENTRY_LOG_LEVEL "${configured}"; using "${DEFAULT_LEVEL}". ` +
      `Valid levels: ${SEVERITIES.join(', ')}.`,
  );
  return DEFAULT_LEVEL;
}

export function consoleMethodsAtOrAbove(threshold: Severity): ConsoleMethod[] {
  const floor = SEVERITIES.indexOf(threshold);
  return (Object.keys(CONSOLE_METHOD_SEVERITY) as ConsoleMethod[]).filter(
    (method) => SEVERITIES.indexOf(CONSOLE_METHOD_SEVERITY[method]) >= floor,
  );
}

export function isSentryLogsEnabled(flag: string | undefined): boolean {
  return (flag ?? '').trim().toLowerCase() === 'true';
}

function truncate(value: string, maxLength = MAX_STRING_LENGTH): string {
  return value.length > maxLength ? `${value.slice(0, maxLength)}…` : value;
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// The SDK runs `normalize()` on console arguments before we see them, which turns an Error
// into a plain `{ message, name, stack, ...ownProps }` object, so `instanceof` no longer works.
function isNormalizedError(value: Record<string, unknown>): boolean {
  return typeof value.name === 'string' && typeof value.message === 'string' && 'stack' in value;
}

type FlattenBudget = { remaining: number };

function toJson(value: unknown): string {
  try {
    return truncate(JSON.stringify(value) ?? String(value));
  } catch {
    return '[UNSERIALIZABLE]';
  }
}

/**
 * Never overwrites: a generated key such as `error.message` can coincide with one the caller
 * already used (e.g. a first-argument object with an `error` field), so a clash gets a suffix.
 */
function setAttr(attrs: Record<string, unknown>, key: string, value: unknown): void {
  if (!Object.hasOwn(attrs, key)) {
    attrs[key] = value;
    return;
  }
  let n = 2;
  while (Object.hasOwn(attrs, `${key}#${n}`)) n += 1;
  attrs[`${key}#${n}`] = value;
}

/**
 * Number of attributes `put` would create for `value`, stopping once it exceeds `limit`.
 * Must mirror the rules in `put`.
 */
function countLeaves(value: unknown, depth: number, limit: number): number {
  if (value === null || value === undefined) return 0;
  if (!isPlainRecord(value) || depth >= MAX_DEPTH) return 1;

  let count = 0;
  for (const nestedValue of Object.values(value)) {
    count += countLeaves(nestedValue, depth + 1, limit - count);
    if (count > limit) break;
  }
  // An object with no fields to split out is kept as one JSON string.
  return Math.max(count, 1);
}

function put(attrs: Record<string, unknown>, key: string, value: unknown, depth: number, budget?: FlattenBudget): void {
  if (value === null || value === undefined) return;

  switch (typeof value) {
    case 'string':
      setAttr(attrs, key, truncate(value));
      return;
    case 'number':
      // Sentry's serializer rejects NaN; keep it visible as text.
      setAttr(attrs, key, Number.isFinite(value) ? value : String(value));
      return;
    case 'boolean':
      setAttr(attrs, key, value);
      return;
  }

  // Arrays, anything past MAX_DEPTH, objects with no non-null fields, and any object whose
  // fields would not fit in the per-log budget collapse to one JSON string rather than fanning
  // out into many attribute keys (or vanishing). The budget is charged once, here, for the
  // whole object, so recursion below passes none.
  const isFlattenable = isPlainRecord(value) && depth < MAX_DEPTH;
  const hasFields = isFlattenable && Object.values(value).some((v) => v !== null && v !== undefined);
  const cost = budget && isFlattenable ? countLeaves(value, depth, budget.remaining) : 0;
  const fitsBudget = !budget || cost <= budget.remaining;

  if (!isFlattenable || !hasFields || !fitsBudget) {
    setAttr(attrs, key, toJson(value));
    return;
  }

  if (budget) budget.remaining -= cost;
  for (const [nestedKey, nestedValue] of Object.entries(value)) {
    put(attrs, `${key}.${nestedKey}`, nestedValue, depth + 1);
  }
}

/**
 * Makes console-captured logs queryable. Reshapes attributes only: it never drops a log and
 * never removes or masks a value. PHI scrubbing is configured server-side in Sentry
 * (Settings → Security & Privacy), not here.
 *
 * Why it is needed: nearly every call site is shaped `console.error('Context:', error)`. The
 * integration stores each non-string argument as `sentry.message.parameter.N`, and the SDK
 * `JSON.stringify`s any object attribute, so without this every Error or payload arrives as
 * one opaque, unqueryable string.
 *
 *   Error argument  -> `error.type`, `error.message`, `error.stack` (same keys as flowhawk-server)
 *   object argument -> `arg.N.<path>` leaves
 *   plain object as first argument (spread by the SDK) -> nested values become `<key>.<path>`
 *
 * `sentry.message.parameter.N` is kept as a short string ("TypeError: bad worker", or the
 * truncated JSON of an object) so `sentry.message.template` still has every parameter it refers
 * to; the fields above are what to query on.
 *
 * At most MAX_FLATTENED_ATTRIBUTES fields are created per log; an object that does not fit is
 * kept as one (truncated) JSON string. The full text is still in the log message.
 *
 * Limitation: an Error passed as the FIRST argument, `console.error(err)`, gets no parameter
 * from the SDK, so it is only in the message. Put a context string first: `console.error('X failed:', err)`.
 */
export function flattenLogAttributes(log: SentryLog): SentryLog {
  const attributes = log.attributes;
  if (!attributes) return log;

  const flat: Record<string, unknown> = {};
  const budget: FlattenBudget = { remaining: MAX_FLATTENED_ATTRIBUTES };
  let errorCount = 0;

  // Values that are already flat go in first. That includes everything the SDK adds itself
  // (`user.*`, `sentry.environment`, `sentry.release`, ...), so those always keep their standard
  // keys, and a generated key that clashes with one of them is the one that gets a suffix.
  // Strings and arrays from the call's arguments still go through `put`, so they get the same
  // limits as everything else (strings truncated, arrays kept as one truncated JSON string)
  // instead of shipping a whole payload, e.g. `console.error('x', cases)` with a large array.
  for (const [key, value] of Object.entries(attributes)) {
    if (isPlainRecord(value)) continue;

    if (isCallerKey(key) && (typeof value === 'string' || Array.isArray(value))) {
      put(flat, key, value, 0, budget);
    } else {
      setAttr(flat, key, value);
    }
  }

  for (const [key, value] of Object.entries(attributes)) {
    if (!isPlainRecord(value)) continue;

    if (key.startsWith(PARAMETER_PREFIX) && isNormalizedError(value)) {
      // A second Error in one call is rare; keep it distinct rather than overwrite the first.
      const prefix = errorCount === 0 ? 'error' : `error.${errorCount}`;
      errorCount += 1;
      const { name, message, stack, ...rest } = value;
      setAttr(flat, key, truncate(`${String(name)}: ${String(message)}`));
      // type/message/stack are always kept; only the Error's extra properties draw on the budget.
      put(flat, `${prefix}.type`, name, 0);
      put(flat, `${prefix}.message`, message, 0);
      if (typeof stack === 'string') {
        setAttr(flat, `${prefix}.stack`, truncate(stack, MAX_STACK_LENGTH));
      }
      for (const [restKey, restValue] of Object.entries(rest)) {
        put(flat, `${prefix}.${restKey}`, restValue, 1, budget);
      }
    } else if (key.startsWith(PARAMETER_PREFIX)) {
      setAttr(flat, key, toJson(value));
      put(flat, `arg.${key.slice(PARAMETER_PREFIX.length)}`, value, 0, budget);
    } else {
      put(flat, key, value, 0, budget);
    }
  }

  return { ...log, attributes: flat };
}

/**
 * Sentry.init options for Sentry Logs. Off unless `VITE_SENTRY_ENABLE_LOGS=true`.
 *
 * `enableLogs` is always an explicit boolean: the SDK types document it as `@default true`,
 * and `_INTERNAL_captureLog` treats `undefined` as enabled, so leaving it unset would let any
 * future `Sentry.logger.*` call ship logs from an environment that never opted in.
 *
 * Callers pass the two values explicitly. Do not default this to `import.meta.env`: reading the
 * whole object makes Vite inline every VITE_* variable into the production bundle.
 */
export function sentryLogsOptions(env: { VITE_SENTRY_ENABLE_LOGS?: string; VITE_SENTRY_LOG_LEVEL?: string }): {
  enableLogs: boolean;
  beforeSendLog?: typeof flattenLogAttributes;
  integrations: ReturnType<typeof Sentry.consoleLoggingIntegration>[];
} {
  if (!isSentryLogsEnabled(env.VITE_SENTRY_ENABLE_LOGS)) {
    return { enableLogs: false, integrations: [] };
  }

  const level = resolveSentryLogLevel(env.VITE_SENTRY_LOG_LEVEL);
  return {
    enableLogs: true,
    beforeSendLog: flattenLogAttributes,
    integrations: [Sentry.consoleLoggingIntegration({ levels: consoleMethodsAtOrAbove(level) })],
  };
}
